import { makeAutoObservable, action } from "mobx"
import { getTelegramWebApp, getTelegramThemeVars } from "../lib/telegram.js"
import type { RootStore } from "./RootStore.js"

export type GalleryLayout = "grid" | "masonry" | "justified"
export type GalleryGap = "none" | "sm" | "md" | "lg"
export type GalleryRadius = "none" | "sm" | "md" | "lg"
export type GalleryFont = "system" | "serif" | "sans-serif" | "mono"
export type CoverStyle = "fullbleed" | "minimal" | "none"

export interface ThemeConfig {
  accentColor: string | null
  fontFamily: GalleryFont
  borderRadius: GalleryRadius
  coverStyle: CoverStyle
}

export interface LayoutConfig {
  type: GalleryLayout
  columns: 2 | 3 | 4
  gap: GalleryGap
  showCaptions: boolean
  captionPosition: "below" | "overlay" | "lightbox-only"
}

const defaultTheme: ThemeConfig = {
  accentColor: null,
  fontFamily: "sans-serif",
  borderRadius: "sm",
  coverStyle: "fullbleed",
}

const defaultLayout: LayoutConfig = {
  type: "grid",
  columns: 3,
  gap: "sm",
  showCaptions: false,
  captionPosition: "lightbox-only",
}

export class ThemeStore {
  colorScheme: "light" | "dark" = "light"
  theme: ThemeConfig = { ...defaultTheme }
  layout: LayoutConfig = { ...defaultLayout }
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this, { rootStore: false })
  }

  init(): void {
    const webapp = getTelegramWebApp()
    if (webapp) {
      this.colorScheme = webapp.colorScheme

      // Apply Telegram CSS variables to document root
      const vars = getTelegramThemeVars()
      for (const [key, value] of Object.entries(vars)) {
        document.documentElement.style.setProperty(key, value)
      }
      document.documentElement.setAttribute("data-tg-theme", this.colorScheme)
    } else {
      // Detect system preference
      this.colorScheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
    }
  }

  applyGalleryTheme = action((theme: Partial<ThemeConfig>, layout: Partial<LayoutConfig>) => {
    this.theme = { ...defaultTheme, ...theme }
    this.layout = { ...defaultLayout, ...layout }

    const root = document.documentElement
    if (this.theme.accentColor) {
      root.style.setProperty("--owner-accent", this.theme.accentColor)
      root.setAttribute("data-accent-color", "true")
    }
    root.setAttribute("data-font", this.theme.fontFamily)
    root.setAttribute("data-radius", this.theme.borderRadius)
    root.setAttribute("data-gap", this.layout.gap)
    root.setAttribute("data-columns", String(this.layout.columns))
  })

  resetTheme = action(() => {
    this.theme = { ...defaultTheme }
    this.layout = { ...defaultLayout }
    document.documentElement.removeAttribute("data-accent-color")
    document.documentElement.removeAttribute("data-font")
    document.documentElement.removeAttribute("data-radius")
    document.documentElement.removeAttribute("data-gap")
    document.documentElement.removeAttribute("data-columns")
  })
}
